import { useOpenIntentDialog } from "@/components/auth/UserIntentModal";
import { useApiError, useTranslation } from "@/hooks";
import { useGetProfile, useUpdateProfile } from "@/services";
import { currentUserAtom, userIntentAtom } from "@/store/login";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form, Input, Typography } from "@st-marketplace/ui";
import { useAtomValue, useSetAtom } from "jotai";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SettingsCard } from "./SettingsCard";
import { SettingsField, SettingsRow } from "./SettingsRow";
import { InputSkeleton } from "./Skeleton";

const accountSchema = z.object({
	username: z.string().min(6).max(32),
});

type AccountFormData = z.infer<typeof accountSchema>;

export const SettingsAccountCard: FC = () => {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);
	const userIntent = useAtomValue(userIntentAtom);
	const setCurrentUser = useSetAtom(currentUserAtom);
	const { getErrorInterface } = useApiError();
	const openIntentDialog = useOpenIntentDialog();

	const updateProfileMutation = useUpdateProfile();
	const {
		data: profileData,
		isLoading,
		error: fetchError,
		refetch,
	} = useGetProfile({ path: { user_id: user?.id || "" } });

	const error = fetchError ? getErrorInterface(fetchError) : null;

	const form = useForm<AccountFormData>({
		resolver: zodResolver(accountSchema),
		values: { username: profileData?.display_name || "" },
	});

	const onSubmit = form.handleSubmit((data) => {
		updateProfileMutation.mutate(
			{
				path: { user_id: user?.id || "" },
				body: { display_name: data.username, password_change: null },
			},
			{
				onSuccess: () => {
					setCurrentUser((prev) =>
						prev ? { ...prev, username: data.username } : prev,
					);
					form.reset({ username: data.username });
				},
			},
		);
	});

	return (
		<>
			<Form {...form}>
				<form noValidate onSubmit={onSubmit}>
					<SettingsCard
						layout="compact"
						title={t("settings.account.title")}
						description={t("settings.account.description")}
						footer={
							<Button
								type="submit"
								disabled={isLoading}
								loading={updateProfileMutation.isPending}
							>
								{t("settings.saveButton")}
							</Button>
						}
					>
						<SettingsRow
							title={t("profile.user.emailLabel")}
							description={t("profile.user.emailDescription")}
						>
							{isLoading ? (
								<InputSkeleton />
							) : (
								<Input
									name="email"
									value={profileData?.email ?? ""}
									disabled
									ltr
								/>
							)}
						</SettingsRow>

						{error ? (
							<SettingsRow title={t("error.tryAgain")}>
								<div className="flex items-center gap-2">
									<Typography variant="body-sm" className="text-danger-text">
										{error.message}
									</Typography>
									<Button
										type="button"
										color="secondary"
										variant="text"
										size="sm"
										onClick={() => refetch()}
									>
										{t("error.tryAgain")}
									</Button>
								</div>
							</SettingsRow>
						) : null}

						<SettingsField
							control={form.control}
							name="username"
							title={t("profile.user.usernameLabel")}
							description={t("profile.user.usernameDescription")}
							render={(field) =>
								isLoading ? (
									<InputSkeleton />
								) : (
									<Input
										{...field}
										ltr
										placeholder={t("profile.user.usernameLabel")}
									/>
								)
							}
						/>

						<SettingsRow
							title={t("auth.userIntent.modeLabel")}
							description={t("auth.userIntent.safeNote")}
						>
							<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
								<Button
									type="button"
									color="secondary"
									size="sm"
									onClick={openIntentDialog}
								>
									{t("auth.userIntent.switchButton")}
								</Button>
								<Typography
									variant="body-sm"
									className="text-secondary-text-100"
								>
									{userIntent === "BUY" && t("profile.userIntent.current.buy")}
									{userIntent === "SELL" &&
										t("profile.userIntent.current.sell")}
									{userIntent === "BOTH" &&
										t("profile.userIntent.current.both")}
									{!userIntent && t("profile.userIntent.current.none")}
								</Typography>
							</div>
						</SettingsRow>
					</SettingsCard>
				</form>
			</Form>
		</>
	);
};
